import { BpIdinviteAuditLogDialog } from "modules/system/sections/plt/bpid/components/BpIdinviteAuditLogDialog";
import React, { FunctionComponent, useMemo, useState } from "react";

import { DateTime } from "@bps/date-time";
import {
  Callout,
  DetailsRow,
  IColumn,
  IconButton,
  Link,
  ScrollablePane,
  SelectionMode,
  Stack,
  Text,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { ScrollListener } from "@components/tables/ScrollListener";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import {
  BpIdAuditLogArgs,
  BpIdAuditLogDto
} from "@libs/api/gateways/bp-id/bp-id.dtos";
import {
  useBpIdUserAuditLogs,
  useCloudApplicationsRefData
} from "@libs/api/gateways/bp-id/bp-id.hooks";
import { config } from "@libs/config/config";
import { getPagesResult } from "@libs/paging/paging.utils";

import { TenantLink } from "./TenantLink";

interface BpIdAuditLogListProps {
  bpIdUserId: string;
  filters: BpIdAuditLogArgs;
}
export const BpIdUserAuditLogList: FunctionComponent<BpIdAuditLogListProps> = ({
  bpIdUserId,
  filters
}) => {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage
  } = useBpIdUserAuditLogs(bpIdUserId, filters);

  const theme = useTheme();
  const items = useMemo(() => getPagesResult(data), [data]);
  const {
    data: cloudApplications = [],
    isLoading: isLoadingCloudApplications,
    error: cloudApplicationsError
  } = useCloudApplicationsRefData();

  const [invitationId, setInvitationId] = useState<string | undefined>();
  const [details, setDetails] = useState<
    { item: BpIdAuditLogDto; target: HTMLElement } | undefined
  >();

  // Seq link for a correlation id, scoped to a +/-2 min window around the event.
  const seqLink = (field: string, value: string, timeStamp: string) => {
    const from = DateTime.fromISO(timeStamp)
      .setZone("utc")
      ?.minus({ minute: 2 })
      ?.toISO();

    const to = DateTime.fromISO(timeStamp)
      .setZone("utc")
      ?.plus({ minute: 2 })
      ?.toISO();
    return `${config.seqUrl}?filter=${field}%20%3D%20'${value}'&from=${from}&to=${to}`;
  };

  const ToolTipContent: FunctionComponent<BpIdAuditLogDto> = item => (
    <Stack>
      {item.journey && (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>Journey</Text>
          <Text>{item.journey}</Text>
        </Stack>
      )}
      {item.policyName && (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>Policy name</Text>
          <Text>{item.policyName}</Text>
        </Stack>
      )}

      {item.step && (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>Step</Text>
          <Text>{item.step}</Text>
        </Stack>
      )}
      {Object.keys(item.properties).map(x => (
        <Stack key={x} horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>{x}</Text>
          <Text>{item.properties[x]}</Text>
        </Stack>
      ))}
      {item.timeStamp && item.b2cCorrelationId && (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>B2C Correlation Id</Text>
          <Link
            href={seqLink(
              "B2CCorrelationId",
              item.b2cCorrelationId,
              item.timeStamp
            )}
            target="_blank"
            rel="noreferrer"
          >
            {item.b2cCorrelationId}
          </Link>
        </Stack>
      )}
      {item.timeStamp && item.correlationId && (
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Text bold>Correlation Id</Text>
          <Link
            href={seqLink(
              "BpTraceRootId",
              (item.correlationId.split(":")[0] ?? "").replace(/\+/g, "%2B"),
              item.timeStamp
            )}
            target="_blank"
            rel="noreferrer"
          >
            {item.correlationId}
          </Link>
        </Stack>
      )}
    </Stack>
  );

  const EventTypeColumn: FunctionComponent<BpIdAuditLogDto> = item =>
    item.invitationId ? (
      <Link onClick={() => setInvitationId(item.invitationId)}>
        {item.eventType}{" "}
      </Link>
    ) : (
      <>{item.eventType}</>
    );

  const columns: IColumn[] = [
    {
      key: "details",
      name: "",
      minWidth: 32,
      maxWidth: 32,
      onRender: (item: BpIdAuditLogDto) => (
        <IconButton
          iconProps={{ iconName: "Info" }}
          title="View details"
          onClick={ev =>
            setDetails({ item, target: ev.currentTarget as HTMLElement })
          }
        />
      )
    },
    {
      key: "timestamp",
      name: "On",
      maxWidth: 110,
      minWidth: 90,
      isResizable: true,
      isMultiline: true,
      onRender: (item: BpIdAuditLogDto) =>
        item.timeStamp ? (
          <Stack>
            <Text>
              {DateTime.fromISO(item.timeStamp).toFormat("dd/MM/yyyy")}
            </Text>
            <Text>{DateTime.fromISO(item.timeStamp).toFormat("HH:mm:ss")}</Text>
          </Stack>
        ) : (
          "-"
        )
    },
    {
      key: "applicationCode",
      name: "Application",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) => {
        if (isLoadingCloudApplications || cloudApplicationsError) {
          return item.applicationCode;
        } else {
          const app = cloudApplications.find(
            x => x.code == item.applicationCode
          );
          return app ? app.text : item.applicationCode;
        }
      }
    },
    {
      key: "tenant",
      name: "Tenant",
      maxWidth: 160,
      minWidth: 100,
      isResizable: true,
      isMultiline: true,
      onRender: (item: BpIdAuditLogDto) => (
        <TenantLink tenantId={item.tenantId} />
      )
    },
    {
      key: "source",
      name: "Source",
      maxWidth: 100,
      minWidth: 80,
      isMultiline: true,
      onRender: (item: BpIdAuditLogDto) => item.source
    },
    {
      key: "severity",
      name: "Severity",
      maxWidth: 90,
      minWidth: 80,
      onRender: (item: BpIdAuditLogDto) => (
        <TooltipHost content={item.severity}>{item.severity}</TooltipHost>
      )
    },
    {
      key: "eventType",
      name: "Event type",
      maxWidth: 110,
      minWidth: 90,
      isMultiline: true,
      onRender: (item: BpIdAuditLogDto) => <EventTypeColumn {...item} />
    },
    {
      key: "eventAction",
      name: "Event Action",
      maxWidth: 110,
      minWidth: 90,
      isResizable: true,
      onRender: (item: BpIdAuditLogDto) => item.eventAction
    },
    {
      key: "ipAddress",
      name: "IP Address",
      maxWidth: 130,
      minWidth: 100,
      isResizable: true,
      onRender: (item: BpIdAuditLogDto) => item.ipAddress
    },
    {
      key: "errorCode",
      name: "Error Code",
      maxWidth: 160,
      minWidth: 80,
      isMultiline: true,
      isResizable: true,
      onRender: (item: BpIdAuditLogDto) =>
        `${item.errorCode ?? ""} ${item.errorMessage ?? ""}`
    }
  ];

  const handleScrolledToBottom = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  };

  return (
    <>
      {" "}
      <BpIdinviteAuditLogDialog
        invitationId={invitationId}
        onDismiss={() => setInvitationId(undefined)}
      />
      {details && (
        <Callout
          target={details.target}
          onDismiss={() => setDetails(undefined)}
          gapSpace={4}
          setInitialFocus
        >
          <Stack
            tokens={{ childrenGap: 8 }}
            styles={{ root: { padding: 16, maxWidth: 460 } }}
          >
            <ToolTipContent {...details.item} />
          </Stack>
        </Callout>
      )}
      {!isLoading && !error && items.length === 0 ? (
        <Text>No recorded activity data</Text>
      ) : (
        <Stack styles={{ root: { position: "relative", height: 420 } }}>
          <ScrollablePane>
            <ShimmeredDetailsList
              items={items}
              enableShimmer={isLoading}
              errorMessage={error?.message}
              columns={columns}
              selectionMode={SelectionMode.none}
              stickyHeader
              onRenderRow={row => {
                if (!row) return null;

                const item: BpIdAuditLogDto = row.item;
                const styles = item.success
                  ? {}
                  : {
                      root: {
                        backgroundColor: theme.semanticColors.errorBackground
                      }
                    };
                return (
                  <Stack styles={styles}>
                    <DetailsRow styles={styles} {...row} />
                  </Stack>
                );
              }}
            />
            <ScrollListener
              hasNextPage={true}
              onScrolledToBottom={handleScrolledToBottom}
            />
          </ScrollablePane>
        </Stack>
      )}
    </>
  );
};
